// Dynamic spots counter for Pro pricing promotion
// This manages the "first 100 customers" offer with real tracking

const SPOTS_CONFIG = {
  INITIAL_SPOTS: 100,
  STARTING_REMAINING: 87, // Real starting number
  SECOND_CHANCE_THRESHOLD: 10, // When to show "last chance" messaging
  STORAGE_KEY: 'radar_promo_spots_cache',
  CACHE_DURATION: 60000, // Cache for 1 minute
  // Use full URL for API endpoint - update this based on your environment
  API_ENDPOINT: process.env.NODE_ENV === 'production'
    ? 'https://your-app-url.com/api/promo-spots'
    : 'http://localhost:3000/api/promo-spots'
};

class SpotsCounter {
  constructor() {
    this.spots = null;
    this.lastUpdated = null;
    this.round = 1; // Track which round we're on (first 100, second 100, etc.)
  }

  // Get current spots from API with caching
  async getCurrentSpots() {
    // Try cache first
    const cached = this.useLocalCache();
    if (cached) {
      return cached;
    }

    try {
      // Fetch fresh data from API
      const response = await fetch(SPOTS_CONFIG.API_ENDPOINT);
      if (response.ok) {
        const data = await response.json();
        this.spots = data.remainingSpots;
        this.round = data.round || 1;
        this.lastUpdated = new Date().toISOString();
        this.saveToLocalStorage();
        return this.formatSpotsData();
      }
    } catch (error) {
      console.log('API fetch failed, using fallback');
    }

    // Fallback if API fails
    this.spots = SPOTS_CONFIG.STARTING_REMAINING;
    this.round = 1;
    this.lastUpdated = new Date().toISOString();
    return this.formatSpotsData();
  }

  useLocalCache() {
    const stored = this.loadFromLocalStorage();

    if (!stored) return null;

    // Check if cache is still valid (within 1 minute)
    const cacheAge = Date.now() - new Date(stored.lastUpdated).getTime();
    if (cacheAge > SPOTS_CONFIG.CACHE_DURATION) {
      return null; // Cache expired
    }

    this.spots = stored.spots;
    this.round = stored.round || 1;
    this.lastUpdated = stored.lastUpdated;

    return this.formatSpotsData();
  }

  formatSpotsData() {
    const isLastChance = this.spots <= SPOTS_CONFIG.SECOND_CHANCE_THRESHOLD && this.spots > 0;
    const isSecondRound = this.round > 1;

    return {
      remainingSpots: this.spots,
      totalSpots: SPOTS_CONFIG.INITIAL_SPOTS,
      round: this.round,
      isLastChance,
      isSecondRound,
      message: this.getDisplayMessage(),
      urgencyLevel: this.getUrgencyLevel()
    };
  }

  getDisplayMessage() {
    if (this.spots === 0) {
      if (this.round === 1) {
        return 'Second chance batch starting soon!';
      }
      return 'All spots claimed';
    }

    if (this.round > 1) {
      if (this.spots <= SPOTS_CONFIG.SECOND_CHANCE_THRESHOLD) {
        return `FINAL ${this.spots} spots — Second chance ending soon`;
      }
      return `Second chance: ${this.spots} spots remaining`;
    }

    if (this.spots <= SPOTS_CONFIG.SECOND_CHANCE_THRESHOLD) {
      return `Only ${this.spots} spots left at this price`;
    }

    return `First 100 customers only — ${this.spots} spots remaining`;
  }

  getUrgencyLevel() {
    if (this.spots <= 5) return 'critical';
    if (this.spots <= SPOTS_CONFIG.SECOND_CHANCE_THRESHOLD) return 'high';
    if (this.spots <= 25) return 'medium';
    return 'normal';
  }

  saveToLocalStorage() {
    if (typeof window !== 'undefined') {
      localStorage.setItem(SPOTS_CONFIG.STORAGE_KEY, JSON.stringify({
        spots: this.spots,
        lastUpdated: this.lastUpdated,
        round: this.round
      }));
    }
  }

  loadFromLocalStorage() {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SPOTS_CONFIG.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  // Clear cache
  clearCache() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SPOTS_CONFIG.STORAGE_KEY);
    }
  }
}

export const spotsCounter = new SpotsCounter();

// React hook for easy usage
import { useState, useEffect } from 'react';

export function usePromoSpots() {
  const [spotsData, setSpotsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpots = async () => {
      setLoading(true);
      const data = await spotsCounter.getCurrentSpots();
      setSpotsData(data);
      setLoading(false);
    };

    fetchSpots();

    // Refresh every 5 minutes
    const interval = setInterval(fetchSpots, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return { spotsData, loading };
}

// For static imports where hooks can't be used
export async function getPromoSpots() {
  return await spotsCounter.getCurrentSpots();
}